/**
 * src/api/consentCache.ts — the only thing that may open the consent gate when
 * the server can't be reached.
 *
 * The gate fails closed. That is right for someone we know nothing about, and
 * wrong for the customer who consented months ago and hit a bad minute of
 * network: blocking them is a total outage — no crates, no returns, no
 * cancellation — which is harsher than an explicit decline, and declining
 * deliberately stays read-only.
 *
 * So a `current` status is remembered, and ONLY a remembered `current` lets
 * anyone past a failed read. Deliberate constraints:
 *
 *   · Only 'current' is ever written. There is no cache entry that means
 *     pending, declined or unknown — absence blocks, which is the safe default.
 *   · Keyed to the Clerk user id and checked on read, so one customer's cached
 *     consent can never open the gate for another on a shared device.
 *   · Expires. A week-old "they were fine" is reasonable evidence they aren't a
 *     fresh unconsented signup; a year-old one isn't.
 *   · Cleared the moment a successful read says anything other than 'current',
 *     so someone who declines can't ride a stale entry.
 *
 * What this deliberately allows: a customer who was current under v1 when new
 * Terms publish, during an outage, gets through on the strength of v1. That is
 * the correct answer — v1 governed the storage they already have, and the
 * server still blocks new storage for a declined account independently.
 *
 * Mirrored by packages/core/src/consentCache.ts in attic-mobile.
 */

const KEY = 'attic.consentKnownCurrent';

/** How long a remembered `current` stays good. */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface CachedConsent {
  userId: string;
  state: 'current';
  at: number;
}

/** Remember that this customer was fully consented. */
export function rememberConsentCurrent(userId: string | null | undefined): void {
  if (!userId) return;
  const entry: CachedConsent = { userId, state: 'current', at: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* best-effort: a failed write just means the gate blocks on the next outage */
  }
}

/** Drop any remembered consent. Called whenever a live read is not `current`. */
export function forgetConsentCurrent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do — a stale entry still expires on its own */
  }
}

/**
 * May this customer pass a failed status read?
 *
 * True only for a fresh, non-expired entry belonging to THIS user. Every other
 * outcome — no entry, malformed entry, different user, expired — is false, so
 * the gate blocks.
 */
export function wasConsentCurrent(userId: string | null | undefined): boolean {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    const entry = JSON.parse(raw) as Partial<CachedConsent>;
    if (entry?.state !== 'current') return false;
    if (entry.userId !== userId) return false;
    if (typeof entry.at !== 'number') return false;
    return Date.now() - entry.at < MAX_AGE_MS;
  } catch {
    return false;
  }
}
